import { Button } from '@creditornot/cb-button';
import { InputContainer } from '@creditornot/cb-form';
import { Input } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import styled from 'styled-components';

import { RFFFieldErrorWrapper } from 'common/components';
import { Typography as T } from 'common/typography';
import validateYupSchema from 'common/utils/validateYupSchema';

import { ProductList, productListValidationSchema } from './validation';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const StyledHeading = styled(T.Heading6)`
  margin-block-end: 12px;
`;

const StyledDescription = styled(T.Body3)`
  margin-block-end: 12px;
`;

const SourceContainer = styled.div`
  margin-block-end: 24px;
`;

const LinkContainer = styled(InputContainer)`
  margin-block-start: 12px;
`;

const StyledButton = styled(Button).attrs({
  size: 'large',
})`
  width: 100%;
`;

const productListSourceOptions = [
  { value: 'file-upload', label: 'File upload' },
  { value: 'link', label: 'Link' },
];

const hasOwnImagesOptions = [
  { value: 'yes', label: 'Yes, I have high quality images of my products.' },
  { value: 'no', label: 'No, I don’t have high quality images of my products.' },
];

const ProductListForm = () => {
  const initialValues: ProductList = { source: 'file-upload', link: '', file: null, hasOwnImages: 'yes' };
  const [selectedSource, setSelectedSource] = useState<string | null>('file-upload');

  const handleSubmit = () => {
    return;
  };

  return (
    <Wrapper>
      <StyledHeading>Product list</StyledHeading>

      <StyledDescription>
        We would like to know what you are offering to your customers - please share your menu or product list with us.
      </StyledDescription>

      <Form<ProductList>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validateYupSchema(productListValidationSchema)}
      >
        {({ handleSubmit }) => (
          <StyledForm onSubmit={handleSubmit}>
            <SourceContainer>
              <InputContainer label="How will you provide your product list?">
                <Field<string | null> name="source">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <Select
                          inputId="source"
                          options={productListSourceOptions}
                          placeholder=""
                          {...input}
                          onChange={newValue => {
                            input.onChange(newValue);
                            setSelectedSource(newValue);
                          }}
                        />
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </InputContainer>

              {selectedSource === 'link' && (
                <LinkContainer>
                  <Field<string> name="link">
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => <Input placeholder="Enter a link to your menu or product list" {...input} />}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>
                </LinkContainer>
              )}
            </SourceContainer>

            <StyledHeading>Product images</StyledHeading>

            <StyledDescription>
              Photos of your products can significantly influence your customers decisions, and we want to make sure to
              set you up for success with good pictures.
            </StyledDescription>

            <InputContainer label="Do you own high quality images of your products that we could use for your Wolt menu?">
              <Field<string | null> name="hasOwnImages">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => <Select inputId="hasOwnImages" options={hasOwnImagesOptions} placeholder="" {...input} />}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </InputContainer>

            <StyledButton type="submit" variant="blue">
              Submit
            </StyledButton>
          </StyledForm>
        )}
      </Form>
    </Wrapper>
  );
};

export default ProductListForm;
